import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTRPC, useTRPCClient } from './trpc'
import type { Holding, MeWallet, Portfolio } from './api-types'
import { formatSol, formatTokenAmount, LAMPORTS_PER_SOL } from './format'
import { SOL_MINT, tokenLogo } from './default-tokens'
import { isAccountHolding } from './networks'
import { useToast } from '@/components/toast'
import { Ticker } from '@/components/ticker'

/** until when the portfolio reads the exchanges, Sonar and the chain live instead of the server's short caches */
let freshUntil = 0
/** the next refreshes read live: called when the account's own money moved (a flow finished, a deposit credited) */
export function markPortfolioFresh(ms = 45_000) {
  freshUntil = Math.max(freshUntil, Date.now() + ms)
}

/**
 * One shared portfolio query (polled) for every screen. A routine poll is
 * served from the server's short caches; only the refreshes right after
 * the account's own activity ask for a live read, which costs seconds
 * (John, Nov 1 2026: "3-5s for a refresh which is very very slow"). The
 * key leaves `fresh` out, so every screen shares one query.
 */
export function usePortfolio(wallet: MeWallet | null, enabled = true) {
  const trpc = useTRPC()
  const client = useTRPCClient()
  const input = { walletIndex: wallet?.index ?? 0 }
  return useQuery({
    queryKey: trpc.portfolio.get.queryKey(input),
    queryFn: () => client.portfolio.get.query({ ...input, fresh: Date.now() < freshUntil }),
    enabled: enabled && wallet !== null,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  })
}

/**
 * Refreshes the portfolio now and again a few seconds later: the last leg
 * of a flow (a Relay fill, a vault credit) can land a moment after the
 * screen says done, so balances move on their own without a reload.
 */
export function useInvalidatePortfolio() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () => {
    markPortfolioFresh()
    const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.portfolio.get.queryKey() })
    void refresh()
    for (const ms of [4_000, 12_000, 30_000]) setTimeout(() => void refresh(), ms)
  }
}

export type BalanceChange = { symbol: string; delta: number; decimals: number; imageUrl?: string }

/** Compares two portfolio snapshots and lists what went up. */
export function balanceIncreases(previous: Portfolio, next: Portfolio): BalanceChange[] {
  const changes: BalanceChange[] = []
  const prevSol = previous.native?.solana?.balance
  const nextSol = next.native?.solana?.balance
  if (prevSol !== undefined && nextSol !== undefined) {
    const delta = (Number(nextSol) - Number(prevSol)) / LAMPORTS_PER_SOL
    if (delta > 0.000001) changes.push({ symbol: 'SOL', delta, decimals: 9, imageUrl: tokenLogo(SOL_MINT) })
  }
  const prevByAddress = new Map(previous.tokens.map((t) => [t.address, t]))
  for (const token of next.tokens) {
    // the account's rows only: the exchanges' ETH and BNB count (Nov 3 2026), the trading wallet's own EVM rows do not
    if (token.isNative || !isAccountHolding(token)) continue
    const before = prevByAddress.get(token.address)
    const beforeBalance = before ? Number(before.balance) : 0
    const delta = (Number(token.balance) - beforeBalance) / 10 ** token.decimals
    if (delta > 0) changes.push({ symbol: (token.symbol ?? 'token').toUpperCase(), delta, decimals: token.decimals, imageUrl: tokenLogo(token.address, token.imageUrl ?? undefined) })
  }
  return changes
}

let lastOwnActivityAt = 0
const mutedUntil = new Map<string, number>()
/**
 * Swaps, sends, deposits and strategy fills change balances too, and they
 * announce themselves (John, Oct 31 2026: one notification per action). The
 * watcher stays quiet for a moment after one with no asset named, and for a
 * while about the assets named: those an in-flight flow of ours will move.
 */
export function noteOwnActivity(symbols?: string[], ms = 5 * 60_000) {
  if (!symbols) {
    lastOwnActivityAt = Date.now()
    return
  }
  const until = Date.now() + ms
  for (const symbol of symbols) if (symbol) mutedUntil.set(symbol.toUpperCase(), until)
}

const listeners = new Set<(changes: BalanceChange[]) => void>()
/** Lets a screen (the deposit modal) react to arrivals the watcher spotted. */
export function subscribeBalanceIncreases(listener: (changes: BalanceChange[]) => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Mounted once in the shell: watches the shared portfolio query and turns
 * a balance increase nothing of ours explains into a toast, the way Sonar
 * announces deposits.
 */
export function useBalanceWatcher(wallet: MeWallet | null) {
  const portfolio = usePortfolio(wallet)
  const toast = useToast()
  const previous = useRef<Portfolio | null>(null)
  useEffect(() => {
    const next = portfolio.data
    if (!next) return
    const prev = previous.current
    previous.current = next
    if (!prev || prev.walletId !== next.walletId) return
    const changes = balanceIncreases(prev, next)
    if (changes.length === 0) return
    if (Date.now() - lastOwnActivityAt < 30_000) return
    // SOL coming back from a private position is a return, not a deposit
    const returning = prev.positions.some((p) => p.status === 'returning')
    const news = changes.filter((change) => (mutedUntil.get(change.symbol) ?? 0) < Date.now())
    for (const change of news) {
      const amount = change.symbol === 'SOL' ? formatSol(change.delta) : formatTokenAmount(change.delta, change.symbol, 6)
      toast('success', <>Received {amount} <Ticker symbol={change.symbol} imageUrl={change.imageUrl} /></>)
    }
    if (!returning && news.length > 0) for (const listener of listeners) listener(news)
  }, [portfolio.data, toast])
}

export function holdingLabel(token: Holding) {
  return (token.symbol ?? '').toUpperCase()
}
